import moment from 'moment';
import round from 'lodash/round';

import { calculateWithHouseSplitPlan } from './custom_amount_plans/house_split';

function applicablePlan(plan, date, user) {
  return (
    moment(plan.startDate) <= date &&
    moment(plan.endDate) >= date &&
    plan.users.find(planUser => planUser.id === user.id)
  );
}

export function findPlan(plans, { currentPlan, date, id, user }) {
  if (id) {
    return plans.find(plan => plan.id === id);
  }

  if (!user || !date) {
    return;
  }

  date = moment(date);
  const matches = [];
  for (const plan of plans) {

    if (applicablePlan(plan, date, user)) {
      if (currentPlan && currentPlan.id === plan.id) {
        return plan;
      }

      matches.push(plan);
    }
  }

  return matches[0];
}

export function findPlansAssignedToUser(plans, user, { commission, includeCurrent } = {}) {
  const assignedPlans = plans.filter((plan) => {
    let assigned = plan.users.map(user => user.id).includes(user.id);

    if (includeCurrent && commission) {
      assigned = assigned || plan.id === commission.plan?.id;
    }

    return assigned;
  });

  return assignedPlans.sort((planA, planB) => {
    if (includeCurrent && planA.id === commission.plan?.id) {
      return -1;
    } else if (includeCurrent && planB.id === commission.plan?.id) {
      return 1;
    } else if (planA.id === user.primaryCommissionPlanId) {
      return -1;
    } else if (planB.id === user.primaryCommissionPlanId) {
      return 1;
    } else {
      return 0;
    }
  });
}

export function calculateAppliedAmount(plan, customCommissionPlanAmount, grossAmount, fullGrossAmount = grossAmount) {
  if (plan.calculationMode === 'use_custom_commission_amount') {
    const multiplier = grossAmount ? grossAmount / fullGrossAmount : 0;
    return multiplier * (customCommissionPlanAmount || 0);
  } else {
    return grossAmount;
  }
}

export function calculateBrokerSplit({
  plan,
  grossAmount,
  amountToApply = grossAmount,

  // startingPlanAmount is the point on the plan to start calculations
  //   If you have a payment:
  //     You are probably trying to find the tiers used to get the payment's actual split, in which
  //     case it should be the payment's previousCommissionPlanTotalAmount
  //   If you have a payable but no payment:
  //     You are probably trying to calculate the split that the next payment will have, in which
  //     case it should be the payable's startingPlanAmountForPayment
  //   If you have neither a payment nor a payable:
  //     You are probably trying to estimate the future splits for an unpaid commission amount, in
  //     which case it should probably be the plan's current total progress
  startingPlanAmount,

  // payables is optional, and should be payables (with payablesPayments) of a commission
  //   Provide this when calculating an estimate for a commission.
  //   If it has been partially paid (or deposited, for payable-based plans), the true split and
  //   tiers of the payment/payables will be included and the remainder of the commission estimated
  //   from the given startingPlanAmount
  payables = [],

  // Not for external use
  originalPlan,
}) {
  // These custom amount mode plans are a kludge, and don't actually care about the custom amount
  // So, always use grossAmount instead of amount to apply
  if (isHouseSplitPlan(plan)) {
    return calculateWithHouseSplitPlan(plan, grossAmount, startingPlanAmount, payables);
  }

  // There is a rare edge case where a user might have been removed from a plan but still has a
  // commission associated with the plan that needs calculation - in those cases, they may not have
  // any progress available so a default of zero is required to avoid NaNs.
  const amountOnPlan = startingPlanAmount || 0;
  let remainingAmountToApply = amountToApply;
  let brokerSplitAmount = 0;

  const adjustments = getRealProgressAdjustments(plan, payables, originalPlan);
  remainingAmountToApply += adjustments.remainingAmountToApply;
  brokerSplitAmount += adjustments.brokerSplitAmount;

  const appliedTiers = [];
  const applyTier = isSplitPlan(plan) ? applySplitPlanTier : applyGrossPlanTier;

  if (remainingAmountToApply < 0) {
    calculateNegativeApplication({
      amountOnPlan,
      appliedTiers,
      applyTier,
      plan,
      remainingAmountToApply,
    });
  } else {
    calculatePositiveApplication({
      amountOnPlan,
      appliedTiers,
      applyTier,
      plan,
      remainingAmountToApply,
    });
  }

  brokerSplitAmount = brokerSplitAmount + appliedTiers.reduce((sum, tier) => sum + tier.amount, 0);
  const effectiveRate = amountToApply === 0 ? 0 : brokerSplitAmount / amountToApply;

  return {
    amount: grossAmount * effectiveRate,
    percent: effectiveRate * 100.0,
    appliedTiers
  };
}

function calculatePositiveApplication({
  amountOnPlan,
  appliedTiers,
  applyTier,
  plan,
  remainingAmountToApply,
}) {
  for (let i = 0; i < plan.tiers.length; i++) {
    const tier = plan.tiers[i];

    if (tier.to && tier.to <= amountOnPlan) {
      continue;
    }

    if (remainingAmountToApply <= 0) {
      break;
    }

    const { appliedAmount, planAmount, tierSplitAmount } = applyTier(tier, amountOnPlan, remainingAmountToApply);
    amountOnPlan += planAmount;
    remainingAmountToApply -= appliedAmount;
    appliedTiers.push({
      ...tier,
      amount: tierSplitAmount,
      index: i,
    });
  }
}

function calculateNegativeApplication({
  amountOnPlan,
  appliedTiers,
  applyTier,
  plan,
  remainingAmountToApply,
}) {
  for (let i = plan.tiers.length - 1; i >= 0; i--) {
    const tier = plan.tiers[i];

    if (tier.from > amountOnPlan) {
      continue;
    }

    if (remainingAmountToApply >= 0) {
      break;
    }

    const { appliedAmount, planAmount, tierSplitAmount } = applyTier(tier, amountOnPlan, remainingAmountToApply);
    amountOnPlan += planAmount;
    remainingAmountToApply -= appliedAmount;
    appliedTiers.push({
      ...tier,
      amount: tierSplitAmount,
      index: i,
    });
  }
}

function getRealProgressAdjustments(plan, payables, originalPlan) {
  const progressionRecords = getRealProgressionRecords(plan, payables);

  let remainingAmountToApply = 0;
  let brokerSplitAmount = 0;

  for (const { commissionPlanAmount, grossAmount, payeeSplitAmount, payeeSplitPercent } of progressionRecords) {
    // House split plans are converted to gross plans before calculations, and store house split as the custom amount
    // So, make sure we use the gross amount for adjustments and not the custom amount
    if (isSplitPlan(plan) || (originalPlan && originalPlan.customAmountMode === 'house_split')) {
      remainingAmountToApply = remainingAmountToApply - grossAmount;
    } else {
      remainingAmountToApply = round(remainingAmountToApply - commissionPlanAmount, 2);
    }

    if (plan.calculationMode === 'use_custom_commission_amount') {
      brokerSplitAmount = brokerSplitAmount + payeeSplitPercent / 100 * commissionPlanAmount;
    } else {
      brokerSplitAmount = brokerSplitAmount + payeeSplitAmount;
    }
  }

  return {
    remainingAmountToApply,
    brokerSplitAmount,
  };
}

function getRealProgressionRecords(plan, payables) {
  if (plan.payableBasedProgress) {
    return payables;
  } else {
    return payables.reduce((payablesPayments, payable) => {
      return [...payablesPayments, ...payable.payablesPayments];
    }, []);
  }
}

export function translatePlanProgress(sourcePlanProgress, sourcePlan, targetPlan, translateFinalTierProgress) {
  for (const [i, sourceTier] of sourcePlan.tiers.entries()) {
    const sourceTierFrom = Math.max(0, sourceTier.from - 0.01);
    const targetTier = targetPlan.tiers[i];

    if (!sourceTier.to) {
      return translateFinalTierProgress(sourcePlanProgress - sourceTierFrom, sourceTier, targetTier);
    } else if (sourceTier.from <= sourcePlanProgress && sourcePlanProgress <= sourceTier.to) {
      const targetTierFrom = Math.max(0, targetTier.from - 0.01);
      const sourceTierProgress = (sourcePlanProgress - sourceTierFrom) / (sourceTier.to - sourceTierFrom);
      return sourceTierProgress * (targetTier.to - targetTierFrom) + targetTierFrom;
    }
  }
}

function applyGrossPlanTier(tier, amountOnPlan, amountToApply) {
  const tierAmount = amountToApply < 0
    ? getNegativeGrossTierAmount({ amountOnPlan, amountToApply, tier })
    : getPositiveGrossTierAmount({ amountOnPlan, amountToApply, tier });

  return {
    appliedAmount: tierAmount,
    planAmount: tierAmount,
    tierSplitAmount: tierAmount * tier.brokerSplit / 100.0
  };
}

function getPositiveGrossTierAmount({
  amountOnPlan,
  amountToApply,
  tier,
}) {
  if (!tier.to || tier.to >= amountOnPlan + amountToApply) {
    return amountToApply;
  } else {
    return tier.to - amountOnPlan;
  }
}

function getNegativeGrossTierAmount({
  amountOnPlan,
  amountToApply,
  tier,
}) {
  if (tier.from === 0 || (tier.from - 0.01) <= (amountOnPlan + amountToApply)) {
    return amountToApply;
  } else {
    return (tier.from - 0.01) - amountOnPlan;
  }
}

function applySplitPlanTier(tier, amountOnPlan, grossAmountToApply) {
  if (grossAmountToApply < 0) {
    return applyNegativeSplitPlanTier({ amountOnPlan, grossAmountToApply, tier });
  } else {
    return applyPositiveSplitPlanTier({ amountOnPlan, grossAmountToApply, tier });
  }
}

function applyPositiveSplitPlanTier({
  amountOnPlan,
  grossAmountToApply,
  tier,
}) {
  let remainingTierGrossAmount, remainingTierSplitAmount;
  if (tier.to) {
    remainingTierSplitAmount = (tier.to || 0) - amountOnPlan;
    remainingTierGrossAmount = remainingTierSplitAmount / tier.brokerSplit * 100.0;
  }

  let appliedGrossAmount, tierSplitAmount;
  if (tier.to && grossAmountToApply > remainingTierGrossAmount) {
    tierSplitAmount = remainingTierSplitAmount;
    appliedGrossAmount = tierSplitAmount / tier.brokerSplit * 100.0;
  } else {
    appliedGrossAmount = grossAmountToApply;
    tierSplitAmount = appliedGrossAmount * tier.brokerSplit / 100.0;
  }

  return {
    appliedAmount: appliedGrossAmount,
    planAmount: tierSplitAmount,
    tierSplitAmount
  };
}

function applyNegativeSplitPlanTier({
  amountOnPlan,
  grossAmountToApply,
  tier,
}) {
  const tierSplitProgress = amountOnPlan - (tier.from - 0.01);
  const tierGrossProgress = tierSplitProgress / tier.brokerSplit * 100.0;

  let appliedGrossAmount;
  if (Math.abs(grossAmountToApply) <= tierGrossProgress) {
    appliedGrossAmount = grossAmountToApply;
  } else {
    appliedGrossAmount = -tierGrossProgress;
  }

  const tierSplitAmount = appliedGrossAmount * tier.brokerSplit / 100.0;

  return {
    appliedAmount: appliedGrossAmount,
    planAmount: tierSplitAmount,
    tierSplitAmount,
  };
}

export function getCurrentTier(plan) {
  const { tiers, userAmount } = plan;

  if (userAmount <= 0) {
    return tiers[0];
  }

  return tiers.find((tier) => {
    return isCurrentTier(tier, userAmount);
  });
}

export function isCurrentTier(tier, userAmount) {
  return tier.from <= userAmount && (!tier.to || tier.to >= userAmount);
}

export function getPlanTypeLabel(realCalculationMode) {
  switch (realCalculationMode) {
  case 'house_split':
    return `House Split (Plan Commissions Only)`;
  case 'use_plan_gross_commission':
    return `Broker's Gross (Plan Commissions Only)`;
  case 'use_total_gross_commission':
    return `Broker's Gross (All Commissions)`;
  case 'use_custom_commission_amount':
    return 'Custom Amount per Commission';
  case 'use_plan_split_commission':
    return `Broker's Split (Plan Commissions Only)`;
  case 'use_total_split_commission':
    return `Broker's Split (All Commissions)`;
  }
}

// NOTE: This is intentionally false for house split plans
export function isSplitPlan(plan) {
  return plan.calculationMode === 'use_plan_split_commission' || plan.calculationMode === 'use_total_split_commission';
}

export function isCustomAmountPlan(plan) {
  // House split plans are special
  return plan.calculationMode === 'use_custom_commission_amount' && !isHouseSplitPlan(plan);
}

export function isHouseSplitPlan(plan) {
  return plan.customAmountMode === 'house_split';
}

export function isTotalPlan(plan) {
  return plan.calculationMode === 'use_total_gross_commission' || plan.calculationMode === 'use_total_split_commission';
}
